import React, { useState, useRef, useEffect } from 'react';
import { PhoneInputProps } from '../../models';
import styles from './phoneInput.module.scss';
import IconsColor from '../../util/iconsColor';

// Country codes data
const countryCodes = [
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+60', country: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: '+66', country: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: '+84', country: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: '+63', country: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: '+62', country: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: '+977', country: 'NP', name: 'Nepal', flag: '🇳🇵' },
  { code: '+880', country: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: '+94', country: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+92', country: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: '+20', country: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: '+27', country: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: '+234', country: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: '+254', country: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '+7', country: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: '+55', country: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: '+54', country: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: '+64', country: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
];

const PhoneInputComponent: React.FC<PhoneInputProps> = ({
  label,
  value,
  onChange,
  onKeyDown,
  onBlur,
  placeholder = 'XXX XXX XXXX',
  disabled = false,
  required = false,
  error,
  warning,
  width = '100%',
  height = '44px',
  name,
  id,
  hideLabel = false,
  noMargin = false,
  defaultCountry = 'VN',
  international = false,
}) => {
  const inputId = id || `phone-input-${label?.toLowerCase().replace(/\s+/g, '-') || 'phone'}`;
  const [selectedCountryCode, setSelectedCountryCode] = useState('+84');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize country code from value or default
  useEffect(() => {
    if (international) {
      if (value && value.startsWith('+')) {
        // Extract country code from value
        const matchedCountry = countryCodes.find(c => value.startsWith(c.code));
        if (matchedCountry) {
          setSelectedCountryCode(matchedCountry.code);
        }
      } else {
        // Set default country
        const defaultCountryData = countryCodes.find(c => c.country === defaultCountry);
        if (defaultCountryData) {
          setSelectedCountryCode(defaultCountryData.code);
        }
      }
    }
  }, [international, defaultCountry, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        setSearchQuery('');
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Calculate dropdown direction based on available viewport space
  useEffect(() => {
    if (dropdownOpen && wrapperRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const dropdownHeight = 340; // max-height of dropdown
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - wrapperRect.bottom;
      const spaceAbove = wrapperRect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownDirection('up');
      } else {
        setDropdownDirection('down');
      }
    }
  }, [dropdownOpen]);

  // Format phone number with spaces (XXX XXX XXX)
  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digit characters
    const digitsOnly = phoneNumber.replace(/\D/g, '');

    // Limit to 10 digits
    const limited = digitsOnly.slice(0, 10);

    // Format with spaces: XXX XXX XXX
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.slice(0, 3)} ${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6)}`;
    }
  };

  // Get raw digits from formatted value
  const getRawDigits = (formattedValue: string): string => {
    return formattedValue.replace(/\D/g, '');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const digitsOnly = getRawDigits(inputValue);

    // Only allow up to 10 digits
    if (digitsOnly.length <= 15) {
      if (international) {
        // Store with selected country code
        const fullNumber = digitsOnly ? `${selectedCountryCode}${digitsOnly}` : '';
        onChange(fullNumber);
      } else {
        // Store the full phone number with default country code (+84)
        const fullNumber = digitsOnly ? `84${digitsOnly}` : '';
        onChange(fullNumber);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].includes(e.keyCode)) {
      onKeyDown?.(e);
      return;
    }

    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) {
      onKeyDown?.(e);
      return;
    }

    // Allow: home, end, left, right
    if (e.keyCode >= 35 && e.keyCode <= 39) {
      onKeyDown?.(e);
      return;
    }

    // Ensure that it is a number and stop the keypress if not
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }

    onKeyDown?.(e);
  };

  // Extract the phone number part (remove country code)
  const getDisplayValue = (): string => {
    if (!value) return '';
    
    if (international) {
      // Try to match against all country codes
      const matchedCountry = countryCodes.find(c => value.startsWith(c.code));
      if (matchedCountry) {
        return value.slice(matchedCountry.code.length);
      }
      // If no match, check if starts with +
      if (value.startsWith('+')) {
        // Find where digits start after +
        const match = value.match(/^\+(\d+)(.*)$/);
        if (match) {
          return match[2] || '';
        }
      }
      return value;
    } else {
      return value.startsWith('84') ? value.slice(2) : value;
    }
  };

  const displayValue = getDisplayValue();
  const formattedValue = formatPhoneNumber(displayValue);

  const handleCountrySelect = (code: string) => {
    setSelectedCountryCode(code);
    setDropdownOpen(false);
    setSearchQuery('');
    
    // Update the value with new country code
    const digitsOnly = getRawDigits(displayValue);
    if (digitsOnly) {
      onChange(`${code}${digitsOnly}`);
    }
  };

  const filteredCountries = countryCodes.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.includes(searchQuery) ||
    country.country.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentCountry = countryCodes.find(c => c.code === selectedCountryCode);

  return (
    <div
      className={styles.phoneInputWrapper}
      style={{ width, marginBottom: noMargin ? 0 : undefined }}
      ref={wrapperRef}
    >
      {!hideLabel && label && (
        <label htmlFor={inputId} className={styles.phoneInputLabel}>
          {label}{required && <span className={styles.requiredIndicator}>*</span>}
        </label>
      )}

      <div className={styles.phoneInputContainer}>
        {international ? (
          <div
            className={styles.countryCode}
            onClick={() => !disabled && setDropdownOpen(!dropdownOpen)}
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <span className={styles.flag}>{currentCountry?.flag || '🌍'}</span>
            <span>{selectedCountryCode}</span>
            <span className={`${styles.arrow} ${dropdownOpen ? styles.arrowUp : ''}`}>▼</span>
          </div>
        ) : (
          <div className={styles.countryCode}>+84</div>
        )}

        <input
          type="text"
          id={inputId}
          name={name || inputId}
          value={formattedValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={styles.phoneInputField + ' ' + (error ? styles.error : '') + ' ' + (warning ? styles.warning : '') + ' ' + (disabled ? styles.disabled : '')}
          style={{ height }}
          inputMode="numeric"
        />
      </div>

      {international && dropdownOpen && (
        <div className={`${styles.dropdown} ${dropdownDirection === 'up' ? styles.dropdownUp : styles.dropdownDown}`} ref={dropdownRef}>
          <input
            type="text"
            placeholder="Search country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
            autoFocus
          />
          <div className={styles.dropdownList}>
            {filteredCountries.map((country) => (
              <div
                key={country.code}
                className={`${styles.dropdownItem} ${selectedCountryCode === country.code ? styles.selected : ''}`}
                onClick={() => handleCountrySelect(country.code)}
              >
                <span className={styles.flag}>{country.flag}</span>
                <span className={styles.countryName}>{country.name}</span>
                <span className={styles.countryCodeText}>{country.code}</span>
              </div>
            ))}
            {filteredCountries.length === 0 && (
              <div className={styles.noResults}>No countries found</div>
            )}
          </div>
        </div>
      )}

      {error && (
        <span className={styles.message + ' ' + styles.errorMessage}>
          <IconsColor
            iconName="errorIcon"
            width="16px"
            height="16px"
            fillColor="none"
            strokeColor="currentColor"
          />
          {error}
        </span>
      )}

      {warning && !error && (
        <span className={styles.message + ' ' + styles.warningMessage}>
          <IconsColor
            iconName="warningIcon"
            width="16px"
            height="16px"
            fillColor="none"
            strokeColor="currentColor"
          />
          {warning}
        </span>
      )}
    </div>
  );
};

export default PhoneInputComponent;
